/**
 * @since 2026-04-18
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import React, { useState } from "react";
import { Box } from "@mui/material";
import EChart from "components/Shared/EChart";
import ChartCard from "components/Shared/ChartCard";
import { filterByPeriod } from "components/Shared/chartUtils";

const PriceAdjustmentPanel = ({ detail }) => {
  const [period, setPeriod] = useState("all");
  if (!detail) return null;
  const { fullAdjClose = [], notAdjClose = [], adjFactor = [] } = detail;

  const fFullAdj = filterByPeriod(fullAdjClose, period);
  const fNotAdj = filterByPeriod(notAdjClose, period);
  const fFactor = filterByPeriod(adjFactor, period);

  // Combined min/max across both series so neither line gets clipped at the
  // edges. ECharts' default axis-fitting can clip extremes when two series of
  // different magnitudes share an axis, especially after back-adjustment puts
  // FullAdj outside NotAdj's range (e.g. CL FullAdj reaches < 0 while NotAdj
  // stays > 0).
  let dataMin = Infinity;
  let dataMax = -Infinity;
  for (const arr of [fFullAdj, fNotAdj]) {
    for (const p of arr) {
      const v = p && p[1];
      if (v == null || !Number.isFinite(v)) continue;
      if (v < dataMin) dataMin = v;
      if (v > dataMax) dataMax = v;
    }
  }
  const haveData = Number.isFinite(dataMin) && Number.isFinite(dataMax);

  // Log scale can't render zero or negative values. Continuous futures with
  // CumDiff back-adjustment can go negative (e.g. CL pre-1990) so fall back
  // to linear whenever any visible value is non-positive.
  const useLog = haveData && dataMin > 0;

  const padded = (lo, hi) => {
    const span = Math.max(hi - lo, Math.abs(hi) * 1e-9);
    const pad = span * 0.05;
    return { min: lo - pad, max: hi + pad };
  };

  let yAxis;
  if (!haveData) {
    yAxis = { type: "value", scale: true, axisLabel: { formatter: (v) => v.toFixed(2) } };
  } else if (useLog) {
    yAxis = {
      type: "log",
      min: dataMin * 0.9,
      max: dataMax * 1.1,
      axisLabel: { formatter: (v) => v.toFixed(2) },
    };
  } else {
    const { min, max } = padded(dataMin, dataMax);
    yAxis = { type: "value", min, max, axisLabel: { formatter: (v) => v.toFixed(2) } };
  }

  const priceOption = {
    animation: false,
    legend: { data: ["FullAdj Close", "NotAdj Close"], bottom: 0 },
    tooltip: { trigger: "axis" },
    grid: { left: 70, right: 30, top: 20, bottom: 60 },
    xAxis: { type: "time" },
    yAxis,
    series: [
      { name: "FullAdj Close", type: "line", showSymbol: false, data: fFullAdj, lineStyle: { width: 1.5 } },
      { name: "NotAdj Close", type: "line", showSymbol: false, data: fNotAdj, lineStyle: { width: 1, opacity: 0.7 } },
    ],
  };

  const factorOption = {
    animation: false,
    tooltip: { trigger: "axis" },
    grid: { left: 50, right: 30, top: 20, bottom: 30 },
    xAxis: { type: "time" },
    yAxis: { type: "value", scale: true },
    series: [
      { name: "AdjFactor", type: "line", showSymbol: false, data: fFactor, lineStyle: { width: 1 } },
    ],
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <ChartCard title="Price" period={period} onPeriodChange={setPeriod}>
        <EChart option={priceOption} height={400} />
      </ChartCard>
      {adjFactor.length > 0 && (
        <ChartCard title="Adjustment factor (FullAdj / NotAdj)">
          <EChart option={factorOption} height={200} />
        </ChartCard>
      )}
    </Box>
  );
};

export default PriceAdjustmentPanel;
